import { createMemo, For, Show } from "solid-js";
import {
  ordreLeviersParCouverture,
  ordreProjets,
  tableScores,
  TRIS,
  type Tri,
} from "../domaine/analyses";
import { tableauDepuisMatrice } from "../export";
import { formaterScore } from "../format";
import { couleursCibles, type Analyse } from "./analyse";
import { Figure } from "./Figure";
import { Matrice } from "./Matrice";

// Hauteur visée : de quoi remplir l'écran sans le dépasser. Si les lignes ne
// tiennent pas lisiblement, la matrice déborde et le cadre défile.
const HAUTEUR = () => Math.min(Math.max(360, window.innerHeight * 0.66), 720);

/**
 * Vue maîtresse : projets × leviers (ADR-0003 § 1 du POC). Toute la donnée de
 * l'onglet 3 sur un écran, à des positions stables ; c'est le tri qui produit
 * l'analyse, d'où le sélecteur d'ordre de la barre et la sériation par défaut.
 */
export function VueLeviers(props: { analyse: Analyse; tri: Tri; surTri: (t: Tri) => void }) {
  const p = () => props.analyse.portefeuille;
  const ordreL = createMemo(() => ordreLeviersParCouverture(p().projets, p().leviers));
  const ordreP = createMemo(() => ordreProjets(props.tri, p().projets, props.analyse.ordres));
  const projet = (i: number) => p().projets[ordreP()[i] ?? -1];
  const levier = (j: number) => p().leviers[ordreL()[j] ?? -1];
  const oui = (i: number, j: number) => {
    const id = levier(j)?.id;
    return id !== undefined && (projet(i)?.leviers.includes(id) ?? false);
  };
  let svg: SVGSVGElement | undefined;

  return (
    <Show
      when={p().dimensions.leviers}
      fallback={
        <div class="entete-vue">
          <h2>Projets × familles de leviers</h2>
          <p class="explication discret">
            Pas de table des familles de leviers dans la donnée chargée.
          </p>
        </div>
      }
    >
      <Figure
        id="projets-leviers"
        titre="Projets × familles de leviers"
        lecture={
          <>
            Familles triées par couverture, projets par{" "}
            {/* La matrice n'a d'intérêt que triée (ADR-0003 § 1 du POC). */}
            <select
              class="choix-en-ligne"
              aria-label="Trier par"
              value={props.tri}
              onChange={(e) => props.surTri(e.currentTarget.value as Tri)}
            >
              <For each={TRIS}>{(t) => <option value={t.id}>{t.libelle}</option>}</For>
            </select>
          </>
        }
        svg={() => svg}
        tableau={() =>
          tableauDepuisMatrice(
            ordreP().map((i) => p().projets[i]?.acronyme ?? ""),
            ordreL().map((j) => p().leviers[j]?.libelle ?? ""),
            // eslint-disable-next-line solid/reactivity -- appelé au clic d'export, pas au rendu
            (i, j) => (oui(i, j) ? "oui" : "non"),
          )
        }
      >
        <Matrice
          titre="Matrice projets × familles de leviers"
          type="presence"
          hauteurCible={HAUTEUR()}
          lignes={ordreP().map((i) => {
            const pr = p().projets[i];
            return {
              libelle: pr?.acronyme ?? "",
              titre: pr?.titre,
              couleurs: pr ? couleursCibles(p(), pr) : [],
            };
          })}
          colonnes={ordreL().map((j) => ({
            libelle: p().leviers[j]?.libelle ?? "",
            titre: p().leviers[j]?.libelleLong,
          }))}
          valeur={(i, j) => (oui(i, j) ? 1 : 0)}
          enAvant={
            props.analyse.misEnAvant
              ? (i) => props.analyse.misEnAvant?.has(ordreP()[i] ?? -1) ?? false
              : null
          }
          ligneChoisie={(() => {
            const k = ordreP().findIndex((i) => p().projets[i]?.ref === props.analyse.selection);
            return k === -1 ? null : k;
          })()}
          surLigne={(i) => props.analyse.selectionner(projet(i)?.ref ?? null)}
          infobulle={(i, j) =>
            `${projet(i)?.acronyme} · ${levier(j)?.libelle} : ${oui(i, j) ? "oui" : "non"}`
          }
          refSvg={(e) => (svg = e)}
        />
        <p class="explication discret sous-vue">
          ● : le projet touche à la famille, sans qu'elle soit forcément centrale.
        </p>
      </Figure>
    </Show>
  );
}

/**
 * Matrice de similarité projets × projets, sériée. Complémentaire du graphe : elle
 * ne perd rien, là où le seuil du graphe cache les paires faibles.
 */
export function VueSimilarite(props: { analyse: Analyse }) {
  const p = () => props.analyse.portefeuille;
  const score = createMemo(() => tableScores(props.analyse.similarites));
  const ordre = () => props.analyse.ordres.similarite;
  const valeur = (i: number, j: number) =>
    i === j ? null : score()(ordre()[i] ?? 0, ordre()[j] ?? 0);
  const etiquettes = createMemo(() =>
    ordre().map((i) => {
      const pr = p().projets[i];
      return {
        libelle: pr?.acronyme ?? "",
        titre: pr?.titre,
        couleurs: pr ? couleursCibles(p(), pr) : [],
      };
    }),
  );
  let svg: SVGSVGElement | undefined;

  return (
    <Figure
      id="matrice-similarite"
      titre="Toutes les paires"
      lecture="Plus c'est foncé, plus les projets se ressemblent. Sans seuil. Les blocs sur la diagonale sont des groupes."
      svg={() => svg}
      tableau={() =>
        tableauDepuisMatrice(
          etiquettes().map((e) => e.libelle),
          etiquettes().map((e) => e.libelle),
          valeur,
          (v) => v.toFixed(4),
        )
      }
    >
      <Matrice
        titre="Matrice de similarité projets × projets"
        type="sequentiel"
        diagonale
        hauteurCible={HAUTEUR()}
        lignes={etiquettes()}
        colonnes={etiquettes()}
        valeur={valeur}
        enAvant={
          props.analyse.misEnAvant
            ? (i) => props.analyse.misEnAvant?.has(ordre()[i] ?? -1) ?? false
            : null
        }
        ligneChoisie={(() => {
          const k = ordre().findIndex((i) => p().projets[i]?.ref === props.analyse.selection);
          return k === -1 ? null : k;
        })()}
        surLigne={(i) => props.analyse.selectionner(p().projets[ordre()[i] ?? -1]?.ref ?? null)}
        infobulle={(i, j, v) =>
          v === null
            ? ""
            : `${etiquettes()[i]?.libelle} ~ ${etiquettes()[j]?.libelle} : ${formaterScore(v)}`
        }
        refSvg={(e) => (svg = e)}
      />
    </Figure>
  );
}
